import { Table } from 'app/components/table/table';
import { ITableColumn, TableModel } from 'app/components/table/table.model';
import { useStore } from 'app/context';
import { observer } from 'mobx-react';
import { useEffect, useMemo, useState } from 'react';
import { AgtFitnessVm } from 'app/stores/ui/agt.fitness.ui.store';
import { LeadNowAbos } from 'app/stores/ui/agt.leadnow.ui.store';
import { amisNowProps } from 'app/stores/ui/agt.amisnow.ui.store';
import _ from 'lodash';
import { Button } from 'app/components/common/Button';
import { FitnessTrackerScopesModal } from './FitnessTrackerScopesModal';
import Config from 'Config';
import { AgtGoogleProfileChangeModel } from 'app/stores/agt.googleprofile.store';

export const FitnessTrackerListeExport = observer(() => {
	const { berichteUiiStore, agtFitnessUiStore, agenturListUiStore, uiStore } = useStore();
	const [hasItems, setHasItems] = useState(false);
	const [items, setItems] = useState([] as AgtFitnessVm[]);

	const tm = useMemo(() => {
		const tm = new TableModel<AgtFitnessVm, number>();
		const cols: ITableColumn<AgtFitnessVm, number>[] = [];

		cols.push(...agtBasicData);
		cols.push(...existsData);
		cols.push(...overviewData);
		cols.push(...googleData);
		cols.push(...amisNowData);
		cols.push(...akumaData);
		cols.push(...mazData);
		cols.push(...facebookData);
		cols.push(...instagramData);
		cols.push(...leadNowData);
		cols.push(...kvmData);
		cols.push(...stsData);

		tm.sortBy = 'agt.name';
		tm.setCols(cols);

		tm.idProperty = 'agtId';
		tm.idType = 'number';

		return tm;
	}, []);

	useEffect(() => {
		agtFitnessUiStore.load().then(() => {
			let items = agtFitnessUiStore.items;
			if (berichteUiiStore.selectedAgt) {
				const agtId = berichteUiiStore.selectedAgt.agtId;
				items = items.filter((i) => i.agt.agtId === agtId);
			} else {
				items = agenturListUiStore.applyFilterOnAgt(items) as AgtFitnessVm[];
			}
			setHasItems(items.length > 0);
			setItems(items);
			tm.setRowData(items);
			tm.sort();
		});
	}, [tm, berichteUiiStore.selectedAgt, agenturListUiStore.filterHasChanged, agenturListUiStore, agtFitnessUiStore.items, agtFitnessUiStore]);

	const handleClick = () => {
		generateJson(items, tm.columns);
	};

	const handleShowScopes = () => {
		uiStore.showModal(uiStore.modalIds.fitnessTrackerScopes);
	};

	const generateJson = (items: AgtFitnessVm[], cols: ITableColumn<AgtFitnessVm, number>[]) => {
		const agtArr: any[] = [];
		// remove all amisNow from cols
		const filteredCols = cols.filter((col) => !col.path.includes('amisNow.item.'));
		const amisNowCols = cols.filter((col) => col.path.includes('amisNow.item.'));

		items.forEach((item) => {
			const agtObj: any = {};
			filteredCols.forEach((col) => {
				let val = _.get(item, col.path);
				const key = col.label.toString();

				if (col.path.includes('stsData')) {
					val = item.stsData;
				}
				if (col.path.includes('kvmMonthly')) {
					val = item.kvmMonthly ? item.kvmMonthly.map((kvmKw: any) => ({ bm: kvmKw.bm, vuInbound: kvmKw.vuInbound })) : null;
				}
				if (col.path.includes('gup.items')) {
					if (item.gup && item.gup.changeHistory) {
						// sort by month to ensure correct change calculation
						const sortedItems = item.gup.changeHistory;
						val = sortedItems.map((monthData: AgtGoogleProfileChangeModel, index: number) => {
							return {
								bm: monthData.bm,
								anzahlBewGesamt: monthData.bew,
								bewDurchschnitt: monthData.bewAvg,
								neueBewertung: monthData.changeNeueBew,
							};
						});
					} else {
						val = null;
					}
				}

				// Handle LeadNow columns with brutto/storno structure
				if (key.startsWith('leadNow') && col.render && !['leadNowBrutto', 'leadNowNetto', 'leadNowStorno', 'leadNowAv', 'leadNowVerm'].includes(key)) {
					// Extract the insurance type from the label (e.g., 'leadNowAv' -> 'av')
					const insuranceType = key.replace('leadNow', '').toLowerCase();
					const leadNowData = item.leadNow?.[insuranceType];

					if (leadNowData) {
						val = {
							brutto: leadNowData.brutto || 0,
							netto: leadNowData.netto || 0,
							storno: leadNowData.storno || 0,
						};
					} else {
						val = { brutto: 0, netto: 0, storno: 0 };
					}
				}

				// Handle LeadNow overall totals - default to 0 if missing
				if (['leadNowBrutto', 'leadNowNetto', 'leadNowStorno'].includes(key) && (val === undefined || val === null)) {
					val = 0;
				}

				agtObj[key] = val;
			});

			// Process amisNow separately and add grouped structure
			if (item.amisNow?.aggregated) {
				const groupedData: { [group: string]: { [label: string]: any }[] } = {};
				const rootData: { [key: string]: any } = {};

				amisNowProps.forEach((prop) => {
					const value = _.get(item, prop.path);
					if (value !== undefined && value !== null) {
						if (prop.group) {
							// Items with group parameter go into grouped structure
							if (!groupedData[prop.group]) {
								groupedData[prop.group] = [];
							}
							groupedData[prop.group].push({
								[prop.shortPath]: value,
							});
						} else {
							// Items without group parameter go to root of amisNow
							rootData[prop.shortPath] = value;
						}
					}
				});

				// Create the amisNow object with both root data and grouped data
				const amisNowObject = { ...rootData, ...groupedData };
				agtObj['amisNow'] = amisNowObject;
			}

			agtArr.push(agtObj);
		});
		const json = JSON.stringify(agtArr);

		// create a file with the json data, trigger download
		const env = Config.currentEnv;
		const timestamp = Date.now();
		const blob = new Blob([json], { type: 'application/json' });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = `ft-export-${env}-${timestamp}.json`;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
	};

	return (
		<>
			{!hasItems ? (
				<div className="pad-1rem">Keine Fitnesstrackerdaten gefunden</div>
			) : (
				<>
					<div className="buttons-row">
						<Button onClick={handleClick}>Export JSON</Button>
						<Button onClick={handleShowScopes}>Datenstand</Button>
					</div>
					<Table stickyHeader={true} tm={tm} allowCsvExport={true} csvExportName="fitness-tracker-export.csv"></Table>
					<FitnessTrackerScopesModal modalId={uiStore.modalIds.fitnessTrackerScopes} entityScopes={agtFitnessUiStore.entityScopes} />
				</>
			)}
		</>
	);
});

// BASIC DATA
const agtBasicData = [
	{
		label: 'agtId',
		path: 'agt.agtId',
	},
	{
		label: 'agt',
		path: 'agt.name',
	},
	{
		label: 'vd',
		path: 'agt.vdId',
	},
	{
		label: 'gs',
		path: 'agt.gsId',
	},
];

// EXISTS DATA
const existsData = [
	{
		label: 'googleExists',
		path: 'googleExists',
	},
	{
		label: 'amisNowExists',
		path: 'amisNowExists',
	},
	{
		label: 'planerExists',
		path: 'planerExists',
	},
	{
		label: 'primeExists',
		path: 'primeExists',
	},
	{
		label: 'mazExists',
		path: 'mazExists',
	},
];

// OVERVIEW
const overviewData = [
	{
		label: 'gupZE',
		path: `gupBewAvg.ze`,
	},
	{
		label: 'amisNowAufgabenZE',
		path: `amisNowQuoteRead.ze`,
	},
	{
		label: 'planerAnzahlWochenNutzungZE',
		path: `planerAnzahlWochenNutzung.ze`,
	},
	{
		label: 'primeEinordnungZE',
		path: `primeEinordnungsquote.ze`,
	},
	{
		label: 'mazBestand',
		path: `mazBestandDurchdringungsquote.ze`,
	},
	{
		label: 'facebookFansZE',
		path: `fbFanAnzahl.ze`,
	},
	{
		label: 'instagramFollowerZE',
		path: 'instaFollower.ze',
	},
	{
		label: 'stsBasicSchulung',
		path: 'stsBasicSchulung',
	},
	{
		label: 'kvmInboundAnzahl',
		path: 'anzahlAnrufeGesamtYtd.ergebnis',
	},
	{
		label: 'leadNowBruttoLeadsAnzahl',
		path: 'leadNowBrutto.ergebnis',
	},
];

// GOOGLE
const googleData = [
	{
		label: 'googleRank',
		path: 'gup.rank',
	},
	{
		label: 'gupNeueBewertung',
		path: 'neueBewertung.ergebnis',
	},
	{
		label: 'gupNeueBewertungZiel',
		path: 'neueBewertung.ziel',
	},
	{
		label: 'gupAnzahlBewertungen',
		path: 'anzBew.ergebnis',
	},
	{
		label: 'gupAnzahlBewertungenZiel',
		path: 'anzBew.ziel',
	},
	{
		label: 'gupAnzahlBewertungenBM',
		path: 'gup.items',
		render: (row: any) => {
			if (!row.data.gup || !row.data.gup.changeHistory) {
				return <></>;
			}
			const gupData = row.data.gup.changeHistory;
			return (
				<>
					{gupData.map((monthData: any, index: number) => {
						return (
							<span key={`${row.data.agt.agtId}_${monthData.bm}`}>
								{monthData.bm} | {monthData.bew} | {monthData.changeNeueBew}
								<br />
							</span>
						);
					})}
				</>
			);
		},
	},
	{
		label: 'gupBewertungDurchschnitt',
		path: `bewAvg.ergebnis`,
	},
	{
		label: 'gupBewertungDurchschnittZiel',
		path: `bewAvg.ziel`,
	},
	{
		label: 'gupMedia',
		path: `imgCount.ergebnis`,
	},
	{
		label: 'gupMediaZiel',
		path: `imgCount.ziel`,
	},
	{
		label: 'gupFA',
		path: `qCount.ergebnis`,
	},
	{
		label: 'gupFAZiel',
		path: `qCount.ziel`,
	},
].map((c) => ({
	...c,
	labelModifier: 'bg-green',
	cellModifier: 'bg-green',
}));

// AMIS NOW
const amisNowData = amisNowProps
	.map((prop) => ({
		label: prop.shortPath,
		path: prop.path === 'amisNow.item.quoteRead' ? 'amisNowQuoteRead.ze' : prop.path,
	}))
	.map((c) => ({
		...c,
		labelModifier: 'bg-blue',
		cellModifier: 'bg-blue',
	}));

/// AKUMA
const akumaData = [
	//  --- isPrime ---
	{
		label: 'isAkumaPrimeUser',
		path: `isAkumaPrimeUser`,
	},
	//  --- PLANER ---
	{
		label: 'planerAnzahlWochenNutzung',
		path: `planerAnzahlWochenNutzung.ergebnis`,
	},
	{
		label: 'planerUebertragungZaehler',
		path: `planerUebertragungquoteAmisNow.ergebnis`,
	},
	{
		label: 'planerUebertragungNenner',
		path: `planerUebertragungquoteAmisNow.ziel`,
	},
	{
		label: 'planerUebertragungQuote',
		path: `planerUebertragungquoteAmisNow.ze`,
	},
	//  --- PRIME ---
	{
		label: 'primeEinordnungZaehler',
		path: `primeEinordnungsquote.ergebnis`,
	},
	{
		label: 'primeEinordnungNenner',
		path: `primeEinordnungsquote.ziel`,
	},
	{
		label: 'primeEinordnungQuote',
		path: `primeEinordnungsquote.ze`,
	},
	{
		label: 'primeAbwahlquoteZaehler',
		path: `primeAbwahlquote.ergebnis`,
	},
	{
		label: 'primeAbwahlquoteNenner',
		path: `primeAbwahlquote.ziel`,
	},
	{
		label: 'primeAbwahlQuote',
		path: `primeAbwahlquote.ze`,
	},
	{
		label: 'primeTerminierungExternZaehler',
		path: 'primeTerminierungExtern.ergebnis',
	},
	{
		label: 'primeTerminierungExternNenner',
		path: `primeTerminierungExtern.ziel`,
	},
	{
		label: 'primeTerminierungExternQuote',
		path: 'primeTerminierungExtern.ze',
	},
	{
		label: 'aktionenAnzahlTopAktionen',
		path: 'aktionenAnzahlTopAktionen',
	},
	{
		label: 'aktionenAnzahlTopAktionenBearbeitet',
		path: 'aktionenAnzahlTopAktionenBearbeitet',
	}, {
		label: 'aktionenAnzahlTopAktionenQuote',
		path: 'aktionenAnzahlTopAktionenQuote',
	}, {
		label: 'aktionenAnzahlTopAufgaben',
		path: 'aktionenAnzahlTopAufgaben',
	},


].map((c) => ({
	...c,
	labelModifier: 'bg-yellow',
	cellModifier: 'bg-yellow',
}));

/// Meine Allianz
const mazData = [
	{
		label: 'mazBestand',
		path: `mazBestandDurchdringungsquote.ergebnis`,
	},
	{
		label: 'mazBestandZiel',
		path: `mazBestandDurchdringungsquote.ziel`,
	},
	{
		label: 'mazNeugeschaeft',
		path: `mazNeuDurchdringungsquote.ergebnis`,
	},
	{
		label: 'mazNeugeschaeftZiel',
		path: `mazNeuDurchdringungsquote.ziel`,
	},
].map((c) => ({
	...c,
	labelModifier: 'bg-blue',
	cellModifier: 'bg-blue',
}));

// Social Media
const facebookData: ITableColumn<AgtFitnessVm, number>[] = [
	{
		label: 'fbStatus',
		path: 'fbStatus',
	},
	{
		label: 'fbFanAnzahl',
		path: 'fbFanAnzahl.ergebnis',
	},
	{
		label: 'fbPostinganzahl',
		path: 'fbPostinganzahl.ergebnis',
	},
	{
		label: 'fbEigenePosts',
		path: 'fbEigenePosts.ergebnis',
	},
	{
		label: 'fbPRAbo',
		path: 'fbPRAbo',
	},
	{
		label: 'fb1890Abo',
		path: 'fb1890Abo',
	},

	{
		label: 'currFbViews',
		path: 'sm.currFb.views',
		sortType: 'number',
	},

];

const instagramData: ITableColumn<AgtFitnessVm, number>[] = [
	{
		label: 'instaStatus',
		path: 'instaStatus',
	},
	{
		label: 'instaFollower',
		path: 'instaFollower.ergebnis',
	},
	{
		label: 'instaPostinganzahl',
		path: 'instaPostinganzahl.ergebnis',
	},
	{
		label: 'instaEigenePosts',
		path: 'instaEigenePosts.ergebnis',
	},
	{
		label: 'instaPRAbo',
		path: 'instaPRAbo',
	},
	{
		label: 'currInstaViews',
		path: 'sm.currInsta.views',
		sortType: 'number',
	},
	{
		label: 'currTiktokViews',
		path: 'sm.currTiktok.views',
		sortType: 'number',
	},
	{
		label: 'tikTokPosts',
		path: 'tikTokPosts.ergebnis',
	},
	{
		label: 'linkedInPosts',
		path: 'linkedInPosts.ergebnis',
	},
	{
		label: 'currLinkedinViews',
		path: 'sm.currLinkedin.views',
		sortType: 'number',
	},
];

// Leadnow
const leadNowData = [
	{
		label: 'leadNowStatus',
		path: 'leadNow.agenturStatus',
	},
	{
		label: 'leadNowBrutto',
		path: 'leadNow.brutto',
	},
	{
		label: 'leadNowNetto',
		path: 'leadNow.netto',
	},
	{
		label: 'leadNowStorno',
		path: 'leadNow.storno',
	},
].concat(
	LeadNowAbos.filter((a) => a.path !== 'av' && a.path !== 'verm').map(({ path: pathKey }) => ({
		label: `leadNow${pathKey.charAt(0).toUpperCase() + pathKey.slice(1)}`,
		path: `leadNow${pathKey.charAt(0).toUpperCase() + pathKey.slice(1)}`,
		render: (row: any) => {
			const brutto = row.data.leadNow?.[pathKey]?.brutto || 0;
			const netto = row.data.leadNow?.[pathKey]?.netto || 0;
			const storno = row.data.leadNow?.[pathKey]?.storno || 0;

			return (
				<span>
					b: {brutto} | n: {netto} | s: {storno}
				</span>
			);
		},
	})),
);

// KVM
const kvmData = [
	// {
	// 	label: 'kvmAnrufeGesamtYtd',
	// 	path: 'anzahlAnrufeGesamtYtd.ergebnis',
	// },
	{
		label: 'kvmMonthly',
		path: 'kvmMonthly',
		render: (row: any) => {
			const d = row.data.kvmMonthly;
			return d ? (
				<>
					{d.map((kvmKw: any) => {
						return (
							<span key={`${row.data.agt.agtId}_${kvmKw.kw}`}>
								{kvmKw.bm} | {kvmKw.vuInbound}
								<br />
							</span>
						);
					})}
				</>
			) : (
				<></>
			);
		},
	},
];

// STS
const stsData = [
	{
		label: 'stsData',
		path: 'stsData.bk',
		render: (row: any) => {
			const d = row.data.stsData;
			return d ? (
				<>
					{d.map((bk: any, i: number) => {
						return (
							<span key={`${bk.bid}-${i}`}>
								{bk.bid} | basic: {bk.basic} | mc: {bk.mc} | bm: {bk.bm}
								<br />
							</span>
						);
					})}
				</>
			) : (
				<></>
			);
		},
	},
];
